import { useEffect, useMemo, useRef } from 'react';
import { ItineraryItem, TripInfo } from '../types/Quotation';
import { buildHotelPointsByDay, stripHotelStayPoints } from '../utils/hotelStayPoints';

type UseHotelAutoPointsArgs = {
  itinerary: ItineraryItem[];
  tripInfo: TripInfo;
  suppressedHotelPoints: Record<number, string[]>;
  setItinerary: React.Dispatch<React.SetStateAction<ItineraryItem[]>>;
};

export const useHotelAutoPoints = ({
  itinerary,
  tripInfo,
  suppressedHotelPoints,
  setItinerary,
}: UseHotelAutoPointsArgs) => {
  const lastHotelAutoPointsRef = useRef<string[][]>([]);

  const hotelAutoPointsByDay = useMemo(() => {
    return buildHotelPointsByDay(itinerary, tripInfo.startDate, tripInfo.places);
  }, [itinerary, tripInfo.places, tripInfo.startDate]);

  useEffect(() => {
    const nextAutoByDay = hotelAutoPointsByDay.map((points, idx) => {
      const suppressed = new Set(suppressedHotelPoints[idx] || []);
      return points.filter((p) => !suppressed.has(p));
    });

    setItinerary((prev) => {
      let changed = false;
      const next = prev.map((day, idx) => {
        const currentPoints = (Array.isArray((day as any).points) ? (day as any).points : []) as string[];
        const desiredAuto = nextAutoByDay[idx] || [];
        const filteredPoints = stripHotelStayPoints(currentPoints);
        const mergedPoints = [...filteredPoints];
        desiredAuto.forEach((p) => {
          if (!mergedPoints.includes(p)) mergedPoints.push(p);
        });
        const same = mergedPoints.length === currentPoints.length
          && mergedPoints.every((p, i) => p === currentPoints[i]);
        if (!same) {
          changed = true;
          return { ...day, points: mergedPoints } as any;
        }
        return day;
      });
      return changed ? next : prev;
    });

    lastHotelAutoPointsRef.current = nextAutoByDay;
  }, [hotelAutoPointsByDay, suppressedHotelPoints, setItinerary]);

  return { hotelAutoPointsByDay };
};
